import React, { useEffect, useState } from 'react';
import { Badge, Button, FormControl, Offcanvas, Table } from 'react-bootstrap';
import { X as FiX } from 'react-bootstrap-icons';
import { ClientOrder } from '../../models/clientOrder';
import { OrderProduct } from '../../models/order';
import { SUPPORTED_CURRENCIES } from '../../models/organization';
import { updateClientOrderServer } from '../../api/clientOrders';
import { getClientById, type Client } from '../../api/clients';
import { getClientOrderReference } from './clientOrderReference';
import styles from '../quotations/Quotations.module.scss';

interface ClientOrderViewProps {
  show: boolean;
  onClose: () => void;
  order: ClientOrder | null;
  organizationId?: string;
  referenceCurrency?: string;
  canWrite?: boolean;
  onConvert?: (order: ClientOrder) => void;
  onReject?: (order: ClientOrder) => void;
  onSaved?: (order: ClientOrder) => void;
  addToast?: (message: string) => void;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  submitted: 'Enviado',
  converted: 'Convertido',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
};

function clientPhone(client: Client | null): string {
  if (!client) return '';
  return (
    String(client.mobile || '').trim() ||
    String(client.phone || '').trim() ||
    String(client.officePhone || '').trim()
  );
}

const ClientOrderView: React.FC<ClientOrderViewProps> = ({
  show,
  onClose,
  order,
  organizationId,
  referenceCurrency = 'USD',
  canWrite = false,
  onConvert,
  onReject,
  onSaved,
  addToast,
}) => {
  const [lines, setLines] = useState<OrderProduct[]>([]);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState<Client | null>(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState(false);

  useEffect(() => {
    setLines(order?.products ? [...order.products] : []);
  }, [order]);

  useEffect(() => {
    if (!show || !organizationId || !order?.clientId) {
      setClient(null);
      setClientLoading(false);
      setClientError(false);
      return;
    }
    let cancelled = false;
    setClient(null);
    setClientError(false);
    setClientLoading(true);
    void getClientById(organizationId, order.clientId)
      .then(next => {
        if (cancelled) return;
        if (!next) {
          setClient(null);
          setClientError(true);
          return;
        }
        setClient(next);
        setClientError(false);
      })
      .catch(() => {
        if (!cancelled) {
          setClient(null);
          setClientError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setClientLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, organizationId, order?.clientId]);

  if (!order) return null;
  const reference = getClientOrderReference(order);
  const canEdit = canWrite && order.status === 'submitted';
  const phone = clientPhone(client);
  const money = (amount: number) => {
    const symbol =
      SUPPORTED_CURRENCIES.find(c => c.code === referenceCurrency)?.symbol ||
      referenceCurrency;
    const rounded = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
    return `${symbol} ${rounded}`;
  };

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" className={styles.drawer}>
      <div className={styles.drawerForm}>
        <Offcanvas.Header className={styles.header}>
          <div className={styles.headerRow}>
            <Offcanvas.Title className={styles.headerTitle}>
              Pedido {reference}
            </Offcanvas.Title>
            <button
              type="button"
              className={styles.headerClose}
              onClick={onClose}
              aria-label="Cerrar"
            >
              <FiX size={22} />
            </button>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body className={styles.body}>
          <div className={styles.viewTop}>
            <div>
              <p className={styles.viewRef}>Pedido {reference}</p>
              <h3 className={styles.viewClientName}>{order.clientName}</h3>
              <Badge bg={order.status === 'submitted' ? 'primary' : 'secondary'}>
                {STATUS_LABEL[order.status] || order.status}
              </Badge>
            </div>
            <div className={styles.viewTotalBlock}>
              <span className={styles.viewTotalLabel}>Total</span>
              <span className={styles.viewTotalValue}>{money(order.total || 0)}</span>
            </div>
          </div>

          <div className={styles.viewMeta}>
            <div className={`${styles.viewMetaItem} ${styles.viewMetaWide}`}>
              <span className={styles.viewMetaLabel}>Cliente</span>
              <span className={styles.viewMetaValue}>
                {clientLoading
                  ? 'Cargando ficha…'
                  : clientError
                    ? 'No se pudo cargar la ficha del cliente'
                    : client?.businessName ||
                      client?.legalName ||
                      order.clientName}
              </span>
            </div>
            {client?.legalName &&
            client.legalName !== (client.businessName || order.clientName) ? (
              <div className={styles.viewMetaItem}>
                <span className={styles.viewMetaLabel}>Razón social</span>
                <span className={styles.viewMetaValue}>{client.legalName}</span>
              </div>
            ) : null}
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>RIF</span>
              <span className={styles.viewMetaValue}>
                {client?.rif || '—'}
              </span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Código</span>
              <span className={styles.viewMetaValue}>
                {client?.code || '—'}
              </span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Correo</span>
              <span className={styles.viewMetaValue}>
                {client?.email || '—'}
              </span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Teléfono</span>
              <span className={styles.viewMetaValue}>{phone || '—'}</span>
            </div>
            {client?.fiscalAddress ? (
              <div className={`${styles.viewMetaItem} ${styles.viewMetaWide}`}>
                <span className={styles.viewMetaLabel}>Dir. fiscal</span>
                <span className={styles.viewMetaValue}>
                  {client.fiscalAddress}
                </span>
              </div>
            ) : null}
            {client?.shippingAddress ? (
              <div className={`${styles.viewMetaItem} ${styles.viewMetaWide}`}>
                <span className={styles.viewMetaLabel}>Dir. envío perfil</span>
                <span className={styles.viewMetaValue}>
                  {client.shippingAddress}
                </span>
              </div>
            ) : null}
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Canal</span>
              <span className={styles.viewMetaValue}>{order.channel || '—'}</span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Pago</span>
              <span className={styles.viewMetaValue}>
                {order.paymentMethod || '—'}
                {order.paymentCurrency ? ` · ${order.paymentCurrency}` : ''}
              </span>
            </div>
            {order.paymentReference ? (
              <div className={styles.viewMetaItem}>
                <span className={styles.viewMetaLabel}>Referencia</span>
                <span className={styles.viewMetaValue}>
                  {order.paymentReference}
                </span>
              </div>
            ) : null}
            <div className={`${styles.viewMetaItem} ${styles.viewMetaWide}`}>
              <span className={styles.viewMetaLabel}>Dirección del pedido</span>
              <span className={styles.viewMetaValue}>
                {order.deliveryAddress || '—'}
              </span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Subtotal</span>
              <span className={styles.viewMetaValue}>
                {money(order.subtotal || 0)}
              </span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>IVA</span>
              <span className={styles.viewMetaValue}>
                {money(order.ivaAmount || 0)}
              </span>
            </div>
          </div>
          {order.paymentProofUrl ? (
            <div className="mt-3 p-3 border rounded bg-body-tertiary">
              <div className="fw-semibold mb-2">Comprobante de pago</div>
              {/\.(png|jpe?g|webp)(\?|$)/i.test(order.paymentProofUrl) ||
              /\.(png|jpe?g|webp)(\?|$)/i.test(
                order.paymentProofStoragePath || ''
              ) ? (
                <a
                  href={order.paymentProofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-inline-block mb-2"
                >
                  <img
                    src={order.paymentProofUrl}
                    alt="Comprobante de pago"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 280,
                      borderRadius: 8,
                      objectFit: 'contain',
                    }}
                  />
                </a>
              ) : null}
              <div>
                <a
                  href={order.paymentProofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Abrir comprobante
                </a>
              </div>
            </div>
          ) : null}
          {order.comment ? (
            <p className={styles.viewComment}>{order.comment}</p>
          ) : null}
          {order.saleId ? (
            <p className={styles.viewComment}>
              Venta {order.saleNumber ? `#${order.saleNumber}` : order.saleId}
            </p>
          ) : null}
          <Table size="sm" className="mt-3">
            <thead>
              <tr>
                <th>Producto</th>
                <th className="text-end">Cant.</th>
                <th className="text-end">Precio</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((p, i) => (
                <tr key={p.productId || i}>
                  <td>
                    {p.name}
                    {p.code ? ` (${p.code})` : ''}
                  </td>
                  <td className="text-end">
                    {canEdit ? (
                      <FormControl
                        type="number"
                        min={0.01}
                        step="any"
                        size="sm"
                        className="text-end ms-auto"
                        style={{ maxWidth: 90 }}
                        value={p.quantity}
                        onChange={e => {
                          const quantity = Number(e.currentTarget.value) || 0;
                          setLines(prev =>
                            prev.map((row, idx) =>
                              idx === i ? { ...row, quantity } : row
                            )
                          );
                        }}
                      />
                    ) : (
                      p.quantity
                    )}
                  </td>
                  <td className="text-end">{money(p.price)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Offcanvas.Body>
        {order.status === 'submitted' ? (
          <div className="d-flex gap-2 p-3 border-top">
            {onReject ? (
              <Button variant="outline-danger" onClick={() => onReject(order)}>
                Rechazar
              </Button>
            ) : null}
            {canEdit && organizationId && order.id ? (
              <Button
                variant="outline-secondary"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const updated = await updateClientOrderServer(
                      organizationId,
                      order.id as string,
                      { products: lines }
                    );
                    onSaved?.(updated);
                    addToast?.('Pedido actualizado');
                  } catch (e: any) {
                    addToast?.(e?.message || 'No se pudo guardar');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Guardar líneas
              </Button>
            ) : null}
            {onConvert ? (
              <Button variant="success" className="ms-auto" onClick={() => onConvert(order)}>
                Convertir a venta
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </Offcanvas>
  );
};

export default ClientOrderView;
